<template>
  <div class="labourer-assignment-table">
    <!-- Loading Overlay -->
    <div v-if="localLoading" class="table-loading-overlay">
      <md-progress-spinner md-mode="indeterminate"></md-progress-spinner>
    </div>

    <!-- Empty State -->
    <div v-if="!localWorkPoints || !localWorkPoints.length" class="empty-state">
      <md-icon class="empty-icon">assignment_late</md-icon>
      <p class="empty-text">No work points available</p>
      <p class="empty-subtext">Please generate takeon numbers first</p>
    </div>

    <!-- Table -->
    <md-table
        v-else
        md-card
        class="assignment-table">

      <md-table-row>
        <md-table-head md-label="Work Point"></md-table-head>
        <md-table-head md-label="Activity"></md-table-head>
        <md-table-head md-label="Takeon Number"></md-table-head>
        <md-table-head md-label="Labourer"></md-table-head>
        <md-table-head md-label="Action"></md-table-head>
      </md-table-row>

      <md-table-row
          v-for="(item, index) in localWorkPoints"
          :key="item.uuid || item.id || index"
          :class="{ 'has-error': index === errorIndex && hasValidationErrors, 'row-highlighted': item.isNew }">

        <!-- Work Point Column -->
        <md-table-cell>
          <div class="cell-content">
<!--            <md-icon class="cell-icon">business</md-icon>-->
            <span class="cell-value">{{ getWorkPointName(item) }}</span>
          </div>
        </md-table-cell>

        <!-- Activity Column -->
        <md-table-cell>
          <div class="cell-content">
<!--            <md-icon class="cell-icon">work</md-icon>-->
            <span class="cell-value">{{ getActivityName(item) }}</span>
          </div>
        </md-table-cell>

        <!-- Takeon Number Column -->
        <md-table-cell>
          <div class="cell-content">
<!--            <md-icon class="cell-icon">confirmation_number</md-icon>-->
            <span class="cell-value takeon-number">{{ item.takeon_number || 'N/A' }}</span>
          </div>
        </md-table-cell>

        <!-- Labourer Column - Display Mode or Edit Mode -->
        <md-table-cell>
          <!-- Display Mode: Show assigned labourer as text -->
          <div v-if="editIndex !== index" class="labourer-display">
            <div v-if="item.labourer && item.labourer.id" class="assigned-labourer">
              <md-chip class="md-accent" @click="startEdit(index)">
                <md-icon>person</md-icon>
                <span>{{ getLabourerDisplayName(item.labourer) }}</span>
                <md-icon class="edit-icon">edit</md-icon>
              </md-chip>
            </div>
            <div v-else class="unassigned-labourer" @click="startEdit(index)">
              <md-button class="md-simple md-just-icon assign-button">
                <md-icon>add_circle</md-icon>
                <span class="assign-text">Assign Labourer</span>
              </md-button>
            </div>
          </div>

          <!-- Edit Mode: Show select input -->
          <div v-else class="labourer-edit-mode">
            <div class="edit-header">
              <span class="edit-title">Select Labourer</span>
              <md-button class="md-icon-button md-simple" @click="cancelEdit(index)">
                <md-icon>close</md-icon>
              </md-button>
            </div>

            <!-- Loading indicator for search -->
            <div v-if="searchingIndex === index" class="search-loading">
              <md-progress-spinner md-mode="indeterminate" :md-diameter="DIAMETER"></md-progress-spinner>
              <span class="search-text">Searching labourers...</span>
            </div>

            <select-input
                v-model="tempLabourer"
                label=""
                :options="labourerOptions"
                :errors="getLabourerErrors(index)"
                :track-by="'labourer_number'"
                name="name"
                :taggable="false"
                :reduce-to-value="false"
                :disabled="savingIndex === index"
                :placeholder="'Type at least 2 characters to search...'"
                @selecting="handleLabourerSelect($event, index)"
                @search="handleLabourerSearch($event, index)"
            />

            <div class="edit-actions">
              <md-button
                  class="md-raised md-primary md-small"
                  @click="saveLabourer(index)"
                  :disabled="!tempLabourer || !tempLabourer.id || savingIndex === index">
                <md-icon>check</md-icon>
                Save
              </md-button>
              <md-button
                  class="md-raised md-default md-small"
                  @click="cancelEdit(index)">
                Cancel
              </md-button>
            </div>

            <validation-error
                v-if="index === errorIndex && hasLabourerError"
                :errors="apiValidationErrors.labourer_id" />
          </div>
        </md-table-cell>

        <!-- Action Column -->
        <md-table-cell>
          <md-button
              class="md-icon-button md-raised md-danger"
              :class="{ 'md-disabled': !item.labourer_id }"
              @click="handleRemoveAssignment(index)"
              :title="getRemoveButtonTitle(item)">
            <md-icon>delete</md-icon>
          </md-button>

          <!-- View Labourer Details Button -->
          <md-button
              v-if="item.labourer && item.labourer.id"
              class="md-icon-button md-raised md-primary"
              @click="viewLabourerDetails(item.labourer)"
              title="View Labourer Details">
            <md-icon>visibility</md-icon>
          </md-button>
        </md-table-cell>
      </md-table-row>
    </md-table>

    <!-- Table Footer with Summary -->
    <div v-if="localWorkPoints && localWorkPoints.length" class="table-footer">
      <div class="summary-stats">
        <div class="stat-item">
          <md-icon>assignment</md-icon>
          <span>Total Workplaces: {{ localWorkPoints.length }}</span>
        </div>
        <div class="stat-item">
          <md-icon>person</md-icon>
          <span>Assigned: {{ assignedCount }}</span>
        </div>
        <div class="stat-item">
          <md-icon>person_outline</md-icon>
          <span>Unassigned: {{ unassignedCount }}</span>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import ValidationError from '@/components/ValidationError.vue';
import SelectInput from '@/components/Inputs/SelectInput.vue';

export default {
  name: 'LabourerAssignmentTable',
  components: {
    SelectInput,
    ValidationError
  },
  props: {
    workPoints: {
      type: Array,
      required: true,
      default: function() {
        return [];
      }
    },
    labourers: {
      type: Array,
      default: function() {
        return [];
      }
    },
    isLoading: {
      type: Boolean,
      default: false
    },
    errorIndex: {
      type: Number,
      default: -1
    },
    apiValidationErrors: {
      type: Object,
      default: function() {
        return {};
      }
    }
  },
  data() {
    return {
      localWorkPoints: [],
      editIndex: -1,
      tempLabourer: null,
      searchingIndex: -1,
      savingIndex: -1,
      searchTimeout: null,
      DEBOUNCE_DELAY: 500,
      DIAMETER:24,
      currentSearchQuery: '',
      currentSearchIndex: -1,
      localLabourers: [],
      localLoading: false
    };
  },
  computed: {
    assignedCount() {
      if (!this.localWorkPoints || !this.localWorkPoints.length) return 0;
      return this.localWorkPoints.filter(function(item) {
        return item && item.labourer_id;
      }).length;
    },

    unassignedCount() {
      if (!this.localWorkPoints || !this.localWorkPoints.length) return 0;
      return this.localWorkPoints.length - this.assignedCount;
    },

    hasValidationErrors() {
      return this.apiValidationErrors &&
          Object.keys(this.apiValidationErrors).length > 0;
    },

    hasLabourerError() {
      return this.apiValidationErrors &&
          this.apiValidationErrors.labourer_id &&
          this.apiValidationErrors.labourer_id.length > 0;
    },

    labourerOptions() {
      if (this.editIndex !== -1 && this.localLabourers && this.localLabourers.length) {
        return this.localLabourers;
      }
      // Return empty array when no search results
      return [];
    }
  },
  watch: {
    workPoints: {
      handler: function(newVal) {
        var oldEditIndex = this.editIndex;
        this.localWorkPoints = this.deepClone(newVal);
        if (oldEditIndex !== -1) {
          this.editIndex = -1;
          this.tempLabourer = null;
          this.localLabourers = [];
        }
      },
      deep: true,
      immediate: true
    }
  },
  mounted() {
    this.localWorkPoints = this.deepClone(this.workPoints);
  },
  methods: {
    deepClone(obj) {
      if (!obj) return [];
      if (!Array.isArray(obj)) return [];

      try {
        return JSON.parse(JSON.stringify(obj));
      } catch (e) {
        console.error('Failed to clone workPoints:', e);
        return [];
      }
    },

    getWorkPointName(item) {
      if (!item) return '';
      if (item.workplace && item.workplace.name) {
        return item.workplace.name;
      }
      if (item.workpoint && item.workpoint.name) {
        return item.workpoint.name;
      }
      return 'N/A';
    },

    getActivityName(item) {
      if (!item) return '';
      if (item.activity && item.activity.name) {
        return item.activity.name;
      }
      return 'N/A';
    },

    getLabourerDisplayName(labourer) {
      if (!labourer) return '';
      if (labourer.name && labourer.labourer_number) {
        return labourer.name + ' (' + labourer.labourer_number + ')';
      }
      return labourer.name || labourer.labourer_number || 'Unknown';
    },

    getLabourerErrors(index) {
      if (index === this.errorIndex && this.apiValidationErrors) {
        return this.apiValidationErrors.labourer_id || [];
      }
      return [];
    },

    getRemoveButtonTitle(item) {
      if (!item || !item.labourer_id) {
        return 'No labourer assigned to remove';
      }
      if (item.labourer && item.labourer.name) {
        return 'Remove ' + item.labourer.name;
      }
      return 'Remove assignment';
    },

    startEdit(index) {
      // Cancel any other editing
      if (this.editIndex !== -1 && this.editIndex !== index) {
        this.cancelEdit(this.editIndex);
      }

      var currentLabourer = this.localWorkPoints[index].labourer;
      this.tempLabourer = currentLabourer ? this.deepClone(currentLabourer) : null;
      this.editIndex = index;
      this.localLabourers = [];
      this.currentSearchQuery = '';
      this.currentSearchIndex = index;

      // Clear any previous search timeout
      if (this.searchTimeout) {
        clearTimeout(this.searchTimeout);
      }
    },

    cancelEdit(index) {
      if (this.editIndex === index) {
        this.editIndex = -1;
        this.tempLabourer = null;
        this.searchingIndex = -1;
        this.savingIndex = -1;
        this.localLabourers = [];
        this.currentSearchQuery = '';
        this.currentSearchIndex = -1;

        if (this.searchTimeout) {
          clearTimeout(this.searchTimeout);
        }
      }
    },

    saveLabourer(index) {
      if (!this.tempLabourer || !this.tempLabourer.id) {
        this.notifyWarning( 'Please select a labourer first');
        return;
      }

      this.savingIndex = index;
      this.$emit('change:labourer', this.tempLabourer, index);

      // Close edit mode after saving
      setTimeout(() => {
        this.editIndex = -1;
        this.tempLabourer = null;
        this.savingIndex = -1;
        this.localLabourers = [];
      }, 300);
    },

    handleLabourerSelect(value, index) {
      // console.log('Labourer selected:', value);
      if (!value || !value.id) {
        return;
      }
      this.tempLabourer = value;
    },

    handleLabourerSearch(query, index) {
      // console.log('Search triggered with query:', query, 'for index:', index);

      // Clear previous timeout
      if (this.searchTimeout) {
        clearTimeout(this.searchTimeout);
      }

      // Don't search if query is too short
      if (!query || query.trim().length < 2) {
        this.localLabourers = [];
        this.searchingIndex = -1;
        return;
      }

      // Set searching state for this row only
      this.searchingIndex = index;
      this.currentSearchQuery = query;

      var self = this;
      this.searchTimeout = setTimeout(function() {
        console.log('Emitting labourer:find event with query:', query, 'index:', index);
        self.$emit('labourer:find', query, index);
      }, this.DEBOUNCE_DELAY);
    },

    // Public method to receive search results from parent
    setSearchResults(labourers, index) {
      console.log('Setting search results for index:', index, 'results:', labourers);

      if (this.editIndex === index) {
        if (labourers && labourers.length) {
          this.localLabourers = this.deepClone(labourers);
          console.log('Updated localLabourers with', labourers.length, 'items');
        } else {
          this.localLabourers = [];
          if (this.currentSearchQuery) {
            this.$store.dispatch('alerts/info', 'No labourers found matching "' + this.currentSearchQuery + '"');
          }
        }
      }

      // Clear searching state
      this.searchingIndex = -1;
    },

    handleRemoveAssignment(index) {
      var item = this.localWorkPoints[index];

      if (!item || !item.labourer_id) {
        this.$store.dispatch('alerts/warning', 'No labourer assigned to this position');
        return;
      }

      var self = this;
      this.$swal.fire({
        title: 'Remove Assignment?',
        text: 'Are you sure you want to remove ' + this.getLabourerDisplayName(item.labourer) + ' from this position?',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Yes, remove',
        cancelButtonText: 'Cancel',
        confirmButtonColor: '#d33'
      }).then(function(result) {
        if (result.value) {
          self.$emit('remove:assignment', index);
        }
      });
    },

    viewLabourerDetails(labourer) {
      if (labourer && labourer.id) {
        this.$emit('view:labourer', labourer);
      }
    }
  }
};
</script>

<style scoped>
/* Keep all existing styles */
.labourer-assignment-table {
  position: relative;
  overflow-x: auto;
}

.assignment-table {
  position: relative;
  min-height: 200px;
  width: 100%;
}

.md-table-row {
  transition: all 0.3s ease;
}

.md-table-row:hover {
  background-color: #f5f5f5;
}

.md-table-row.has-error {
  background-color: #ffebee;
  border-left: 3px solid #f44336;
}

.cell-content {
  display: flex;
  align-items: center;
  gap: 8px;
}

.cell-icon {
  font-size: 18px;
  color: #9e9e9e;
  flex-shrink: 0;
}

.takeon-number {
  font-family: monospace;
  font-weight: 600;
  color: #2196f3;
}

.labourer-display {
  position: relative;
  min-height: 48px;
}

.assigned-labourer {
  cursor: pointer;
  display: inline-block;
}

.assigned-labourer .md-chip {
  cursor: pointer;
  transition: all 0.2s ease;
}

.assigned-labourer .md-chip:hover {
  background-color: #2196f3;
  transform: scale(1.02);
}

.edit-icon {
  font-size: 16px;
  margin-left: 4px;
  opacity: 0.7;
}

.unassigned-labourer {
  cursor: pointer;
}

.assign-button {
  margin: 0 !important;
  padding: 0 !important;
  color: #9e9e9e;
}

.assign-button:hover {
  color: #2196f3;
}

.assign-text {
  margin-left: 4px;
  font-size: 12px;
}

.labourer-edit-mode {
  background: white;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  padding: 12px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
  min-width: 280px;
}

.edit-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
  padding-bottom: 8px;
  border-bottom: 1px solid #e0e0e0;
}

.edit-title {
  font-weight: 500;
  font-size: 14px;
  color: #333;
}

.edit-actions {
  display: flex;
  gap: 8px;
  margin-top: 12px;
}

.edit-actions .md-button {
  margin: 0;
  flex: 1;
}

.md-button.md-small {
  font-size: 12px;
  height: 32px;
  min-height: 32px;
}

.search-loading {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px;
  background: #f5f5f5;
  border-radius: 4px;
  margin-bottom: 12px;
}

.search-text {
  font-size: 12px;
  color: #666;
}

.table-loading-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(255, 255, 255, 0.8);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 10;
}

.empty-state {
  text-align: center;
  padding: 60px 20px;
  color: #9e9e9e;
}

.empty-icon {
  font-size: 64px;
  width: 64px;
  height: 64px;
  margin-bottom: 16px;
  color: #e0e0e0;
}

.table-footer {
  padding: 16px 20px;
  background: #fafafa;
  border-top: 1px solid #e0e0e0;
  border-radius: 0 0 4px 4px;
}

.summary-stats {
  display: flex;
  gap: 24px;
  flex-wrap: wrap;
}

.stat-item {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  color: #666;
}

@media (max-width: 768px) {
  .labourer-assignment-table {
    overflow-x: scroll;
  }

  .assignment-table {
    min-width: 600px;
  }

  .labourer-edit-mode {
    min-width: 240px;
  }
}

::v-deep .md-table-head {
  font-weight: 600;
  background-color: #f5f5f5;
  color: #333;
  padding: 12px 16px;
}

::v-deep .md-table-cell {
  padding: 12px 16px;
}

::v-deep .multiselect__tags {
  min-height: 36px;
  border-radius: 4px;
}
</style>